import React, {useEffect, useState} from "react";
import localforage from "localforage";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import Swal from "sweetalert2";

interface Task {
    id: number;
    title: string;
    description: string;
    status: 'todo' | 'in_progress' | 'done';
    due_date: string | null;
    created: string;
}

const COLUMNS: { key: Task['status']; label: string; color: string }[] = [
    { key: 'todo', label: 'To Do', color: '#f59e0b' },
    { key: 'in_progress', label: 'In Progress', color: '#0ea5e9' },
    { key: 'done', label: 'Done', color: '#22c55e' },
];

const TaskBoard: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [pageTitle] = useState("My Tasks")
    const [clinicName, setClinicName] = useState<any>("Facility")
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', due_date: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(()=>{
        document.title = "Uzima - Tasks"
        localforage.getItem("clinic_name").then((clini)=>{
            setClinicName(clini)
        })
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, [])

    const loadTasks = async () => {
        if (!staffId) return;
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff-tasks/?staff_id=${staffId}`));
            const data = await res.json();
            if (data.status === 'success') setTasks(data.data || []);
        } catch { /* leave list as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTasks(); }, [staffId]); // eslint-disable-line react-hooks/exhaustive-deps

    const createTask = async () => {
        if (!form.title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Title Required', text: 'Give the task a title.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff-tasks/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, staff_id: staffId, ...form, due_date: form.due_date || null }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setShowCreate(false);
                setForm({ title: '', description: '', due_date: '' });
                loadTasks();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to create task.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while creating task.' });
        } finally {
            setSubmitting(false);
        }
    };

    const moveTask = async (task: Task, newStatus: Task['status']) => {
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff-tasks/${task.id}`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) loadTasks();
        } catch { /* best-effort */ }
    };

    const deleteTask = async (task: Task) => {
        const confirm = await Swal.fire({ title: 'Delete this task?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff-tasks/${task.id}`), { method: 'DELETE' });
            if (res.ok) loadTasks();
        } catch { /* best-effort */ }
    };

    return (
        <>
            <DashboardHeader></DashboardHeader>
            <SideMenu></SideMenu>

            <div className={"vertical-overlay"}></div>

            <div className={"main-content"}>
                <div className={"page-content"}>

                    <div className={"container-fluid"}>

                        <div className="row">
                            <div className="col-12">
                                <div
                                    className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0  ">{clinicName} - {pageTitle} &nbsp;
                                    </h4>

                                    <div className="page-title-right d-flex align-items-center gap-3">
                                        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
                                            <i className="ri-add-line me-1"></i>New Task
                                        </button>
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a
                                                href="/institution/home">Dashboards</a></li>
                                            <li className="breadcrumb-item active">Taskboard</li>
                                        </ol>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                            </div>
                        ) : (
                            <div className="row">
                                {COLUMNS.map(col => (
                                    <div className="col-lg-4" key={col.key}>
                                        <div className="card">
                                            <div className="card-header d-flex align-items-center justify-content-between" style={{ borderTop: `3px solid ${col.color}` }}>
                                                <h6 className="mb-0">{col.label}</h6>
                                                <span className="badge bg-light text-dark">{tasks.filter(t => t.status === col.key).length}</span>
                                            </div>
                                            <div className="card-body" style={{ minHeight: 300 }}>
                                                {tasks.filter(t => t.status === col.key).length === 0 ? (
                                                    <p className="text-muted small text-center py-4">No tasks here</p>
                                                ) : (
                                                    tasks.filter(t => t.status === col.key).map(task => (
                                                        <div className="card border shadow-none mb-2" key={task.id}>
                                                            <div className="card-body p-3">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <h6 className="mb-1">{task.title}</h6>
                                                                    <button className="btn btn-sm btn-link text-danger p-0" onClick={() => deleteTask(task)}><i className="ri-delete-bin-line"></i></button>
                                                                </div>
                                                                {task.description && <p className="text-muted small mb-2">{task.description}</p>}
                                                                {task.due_date && <p className="text-muted small mb-2"><i className="ri-calendar-line me-1"></i>Due {new Date(task.due_date).toLocaleDateString()}</p>}
                                                                <div className="d-flex gap-1">
                                                                    {COLUMNS.filter(c => c.key !== task.status).map(c => (
                                                                        <button key={c.key} className="btn btn-sm btn-outline-secondary" onClick={() => moveTask(task, c.key)}>
                                                                            Move to {c.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>

                </div>

                <DashboardFooter></DashboardFooter>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Task</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Due Date</label>
                                    <input type="date" className="form-control" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={createTask}>{submitting ? 'Creating...' : 'Create Task'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default TaskBoard
